/**
 * §13.1.9 Defeat — cause-of-death report. All four fields render or the screen is a
 * bug. Retry is the first focus stop and the first tab target, so restart is one tap
 * from the panel appearing (§13.1.9: ≤ 2 clicks, ≤ 5 s).
 *
 * The captain card sits above the report, between the title and the killing
 * blow. It is first because it is the thing worth remembering: a 3× portrait, the
 * name in Silkscreen, and the last thing they said, typed out. The report below is
 * the forensics, and it is what you read *second*. Nothing moved to make room and
 * nothing was added to the run HUD; the whole feature is spent here.
 */

import { CaptainCard } from '../captain-card';
import type { Captain } from '../../captain/seed';
import { button, delta, div, panel, panelBody, setDelta, setText, span } from '../dom';
import { formatInt, formatSeconds, formatTime } from '../format';
import type { DefeatReport } from '../types';
import { Screen, type ActionSink } from './base';

export class DefeatScreen extends Screen {
  private killLine: HTMLElement;
  private killName: HTMLElement;
  private killDelta: HTMLElement;
  private killWhere: HTMLElement;
  private minimapDot: HTMLElement;
  private damageList: HTMLElement;
  private hadLine: HTMLElement;
  private offeredLine: HTMLElement;
  private counterLine: HTMLElement;
  private seed = 0;
  private readonly captainCard = new CaptainCard();
  private readonly reportWrap: HTMLElement;

  constructor(emit: ActionSink) {
    super('defeat', emit, 'is-modal');

    const p = panel('defeat-panel');
    const body = panelBody(p);
    body.appendChild(div('screen-title danger', 'THE LIGHT GOES OUT'));
    body.appendChild(this.captainCard.root);

    // The forensics. Grouped so it can settle in a beat after the card, which is the
    // only reason the card reads as the headline rather than as a fifth report block.
    const report = div('defeat-report');
    this.reportWrap = report;
    body.appendChild(report);

    // 1 — killing blow
    const one = div('report-block');
    one.appendChild(div('report-head', 'Killing blow'));
    // The subject is underlined and the damage is a red-pink bracketed delta — the
    // dwarf.land row shape, applied to the one line that explains the run's end.
    this.killLine = div('report-line');
    this.killName = span('log-subject', '');
    this.killDelta = delta('', 'hp');
    this.killLine.appendChild(this.killName);
    this.killLine.appendChild(this.killDelta);
    one.appendChild(this.killLine);
    const whereWrap = div('report-where');
    this.killWhere = div('report-sub', '');
    const map = div('minimap');
    this.minimapDot = div('minimap-dot');
    map.appendChild(this.minimapDot);
    whereWrap.appendChild(this.killWhere);
    whereWrap.appendChild(map);
    one.appendChild(whereWrap);
    report.appendChild(one);

    // 2 — last 10 s
    const two = div('report-block');
    two.appendChild(div('report-head', 'Last 10 seconds'));
    this.damageList = div('report-list');
    two.appendChild(this.damageList);
    report.appendChild(two);

    // 3 — what you had
    const three = div('report-block');
    three.appendChild(div('report-head', 'What you had'));
    this.hadLine = div('report-line', '');
    three.appendChild(this.hadLine);
    report.appendChild(three);

    // 4 — what the run offered
    const four = div('report-block');
    four.appendChild(div('report-head', 'What the run offered'));
    this.offeredLine = div('report-line', '');
    this.counterLine = div('report-sub', '');
    four.appendChild(this.offeredLine);
    four.appendChild(this.counterLine);
    report.appendChild(four);

    const menu = div('defeat-menu');
    const retry = button('Retry (same seed)', 'primary', 'refresh');
    retry.addEventListener('click', () => this.emit({ type: 'retry_same_seed', seed: this.seed }));
    const newRun = button('New Run', '', 'play');
    newRun.addEventListener('click', () => this.emit({ type: 'new_run' }));
    const summary = button('Summary', '', 'chart-line');
    summary.addEventListener('click', () => this.emit({ type: 'goto', screen: 'summary' }));
    const title = button('Title', 'ghost', 'home');
    title.addEventListener('click', () => this.emit({ type: 'goto', screen: 'title' }));
    menu.appendChild(retry);
    menu.appendChild(newRun);
    menu.appendChild(summary);
    menu.appendChild(title);
    body.appendChild(menu);

    this.root.appendChild(p);
    this.stops = [retry, newRun, summary, title];
  }

  /**
   * The captain, their last line, and how long they lasted.
   *
   * Called before `present` so the card is populated when the panel first paints;
   * `CaptainCard` is idempotent per run seed, so the second call site (the integrator's
   * explicit report) cannot restart the typewriter mid-reveal.
   */
  presentCaptain(captain: Captain, deathLine: string | null, runTimeS: number): void {
    this.captainCard.present(captain, deathLine, runTimeS);
  }

  setReducedMotion(on: boolean): void {
    this.captainCard.setReducedMotion(on);
  }

  /** A new run started; the captain on the card is gone. */
  resetCaptain(): void {
    this.captainCard.reset();
  }

  present(report: DefeatReport, seed: number, arenaHalfW: number, arenaHalfH: number): void {
    this.seed = seed >>> 0;

    setText(this.killName, report.killing_blow.source_name);
    setDelta(this.killDelta, '-' + formatInt(report.killing_blow.damage) + ' HP', 'hp');
    setText(
      this.killWhere,
      't=' +
        formatTime(report.killing_blow.at_time_s) +
        '  (' +
        report.killing_blow.pos.x.toFixed(0) +
        ', ' +
        report.killing_blow.pos.y.toFixed(0) +
        ')',
    );

    const nx = arenaHalfW > 0 ? (report.killing_blow.pos.x / arenaHalfW) * 0.5 + 0.5 : 0.5;
    const ny = arenaHalfH > 0 ? (report.killing_blow.pos.y / arenaHalfH) * 0.5 + 0.5 : 0.5;
    this.minimapDot.style.left = (Math.max(0, Math.min(1, nx)) * 100).toFixed(1) + '%';
    this.minimapDot.style.top = ((1 - Math.max(0, Math.min(1, ny))) * 100).toFixed(1) + '%';

    while (this.damageList.firstChild) this.damageList.removeChild(this.damageList.firstChild);
    for (let i = 0; i < report.last_10s.length; i++) {
      const s = report.last_10s[i];
      // Subject then coloured value, dwarf.land's row shape. The bracketed delta *is*
      // the value column — printing the bare number beside it says the same thing twice.
      const row = div('report-row');
      row.appendChild(span('report-row-name log-subject', s.source_name));
      row.appendChild(delta('-' + formatInt(Math.round(s.amount)) + ' HP', 'hp'));
      this.damageList.appendChild(row);
    }

    const had = report.what_you_had;
    while (this.hadLine.firstChild) this.hadLine.removeChild(this.hadLine.firstChild);
    this.hadLine.appendChild(span('log-subject', 'Lumensurge'));
    this.hadLine.appendChild(
      had.surge_tier_lost > 0 ? delta('-' + formatInt(had.surge_tier_lost) + ' tiers', 'surge') : delta('held', 'xp'),
    );
    this.hadLine.appendChild(span('log-subject', 'Dash'));
    this.hadLine.appendChild(
      had.dash_ready
        ? delta('ready', 'xp')
        : delta(formatSeconds(had.dash_seconds_remaining) + 's from ready', 'dim'),
    );

    const offered = report.what_was_offered;
    while (this.offeredLine.firstChild) this.offeredLine.removeChild(this.offeredLine.firstChild);
    this.offeredLine.appendChild(span('log-subject', 'Health pickups on field'));
    this.offeredLine.appendChild(delta(formatInt(offered.health_pickups_alive) + ' alive', 'xp'));
    setText(this.counterLine, offered.counterplay);
  }

  /**
   * The report settles a beat after the card. Without the delay the eye lands on four
   * headings and a minimap and never reaches the face, which is the one thing on this
   * screen anybody is supposed to remember. The class is toggled off and forced
   * through a reflow so the animation replays on every death, not just the first.
   */
  override onShow(): void {
    this.reportWrap.classList.remove('is-settled');
    void this.reportWrap.offsetWidth;
    this.reportWrap.classList.add('is-settled');
  }

  /** Any way out of the screen snaps the reveal, so nothing types into a fade. */
  override onHide(): void {
    this.captainCard.finish();
  }

  override onBack(): boolean {
    this.emit({ type: 'goto', screen: 'title' });
    return true;
  }
}
